import { BodyProfileHint } from "@/lib/health/use-body-profile";
import { m } from "@/paraglide/messages";
import { type BmiCategory, bmi, bmiCategory } from "../lib/health";
import { HealthDisclaimer } from "./HealthDisclaimer";
import { HeightInput, UnitsToggle, WeightInput } from "./inputs";
import { useBody } from "./useBody";

function categoryLabel(c: BmiCategory): string {
  switch (c) {
    case "underweight":
      return m.bmi_cat_underweight();
    case "normal":
      return m.bmi_cat_normal();
    case "overweight":
      return m.bmi_cat_overweight();
    default:
      return m.bmi_cat_obese();
  }
}

const CATEGORY_COLOR: Record<BmiCategory, string> = {
  underweight: "text-sky-400",
  normal: "text-emerald-400",
  overweight: "text-amber-400",
  obese: "text-rose-400",
};

export function BmiCalc() {
  const b = useBody();
  const value = bmi(b.weightKg, b.heightCm);
  const cat = value !== null ? bmiCategory(value) : null;

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className="mb-5">
        <UnitsToggle value={b.units} onChange={b.setUnits} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <HeightInput
          units={b.units}
          cm={b.cm}
          ft={b.ft}
          inch={b.inch}
          onCm={b.setCm}
          onFt={b.setFt}
          onInch={b.setInch}
          idPrefix="bmi"
        />
        <WeightInput
          units={b.units}
          kg={b.kg}
          lb={b.lb}
          onKg={b.setKg}
          onLb={b.setLb}
          idPrefix="bmi"
        />
      </div>

      <div className="mt-6" aria-live="polite">
        {value === null || cat === null ? (
          <p className="text-sm text-muted-foreground">{m.bmi_enter()}</p>
        ) : (
          <div className="rounded-xl bg-primary/10 p-5 text-center">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {m.bmi_your_bmi()}
            </p>
            <p
              data-testid="bmi-value"
              className="mt-1 font-display text-4xl font-bold tabular-nums text-primary"
            >
              {value.toFixed(1)}
            </p>
            <p
              data-testid="bmi-category"
              className={`mt-1 text-sm font-semibold ${CATEGORY_COLOR[cat]}`}
            >
              {categoryLabel(cat)}
            </p>
          </div>
        )}
      </div>

      <BodyProfileHint {...b.profileSync} />

      <HealthDisclaimer />
    </div>
  );
}
